import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useCurrentCharacterName } from "../hooks/useCurrentCharacterName";
import { api } from "../lib/api";
import { AppError, logError, toUserMessage } from "../lib/errors";
import {
  clearGroupTalkActiveSessionSnapshot,
  loadGroupTalkActiveSessionSnapshot,
  saveGroupTalkActiveSessionSnapshot,
  type GroupTalkActiveSessionSnapshot,
} from "../lib/groupTalkSessionStorage";
import {
  normalizeGroupTalkIntakeView,
  normalizeGroupTalkSessionView,
  normalizeGroupTalkTurnView,
  type GroupTalkTranscriptMessage,
  type GroupTalkTurnView,
} from "../lib/groupTalkView";

const EXPERT_MESSAGE_DELAY_MS = 2000;
const SUMMARY_MESSAGE_DELAY_MS = 1500;

function createPendingTurn(userMessage: string): GroupTalkTurnView {
  const createdAt = new Date().toISOString();
  const userRow: GroupTalkTranscriptMessage = {
    messageId: `pending_user_${Date.now()}`,
    participantId: null,
    memberId: null,
    speakerId: "user",
    speakerName: "あなた",
    speakerType: "user",
    roleLabel: "ユーザー",
    stanceId: null,
    stanceLabel: null,
    facilitator: false,
    phase: "user",
    sequence: 0,
    content: userMessage,
    createdAt,
  };
  const pendingRow: GroupTalkTranscriptMessage = {
    messageId: `pending_meeting_${Date.now()}`,
    participantId: null,
    memberId: null,
    speakerId: "meeting",
    speakerName: "会議メンバー",
    speakerType: "facilitator",
    roleLabel: "ファシリテータ",
    stanceId: null,
    stanceLabel: null,
    facilitator: true,
    phase: "thinking",
    sequence: null,
    content: "参加メンバーが順番に考えています...",
    createdAt,
  };

  return {
    sessionId: "",
    turnId: `pending_turn_${Date.now()}`,
    messages: [userRow, pendingRow],
    createdAt,
    isSatisfied: false,
  };
}

function wait(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function splitMessagesForReveal(messages: GroupTalkTranscriptMessage[]) {
  const userMessages: GroupTalkTranscriptMessage[] = [];
  const intakeMessages: GroupTalkTranscriptMessage[] = [];
  const expertMessages: GroupTalkTranscriptMessage[] = [];
  const summaryMessages: GroupTalkTranscriptMessage[] = [];

  messages.forEach((message) => {
    if (message.phase === "user") {
      userMessages.push(message);
      return;
    }
    if (message.phase === "intake") {
      intakeMessages.push(message);
      return;
    }
    if (message.phase === "summary") {
      summaryMessages.push(message);
      return;
    }
    if (message.phase === "expert") {
      expertMessages.push(message);
    }
  });

  return {
    initialMessages: [...userMessages, ...intakeMessages],
    expertMessages,
    summaryMessages,
  };
}

function mergeTurnMessages(intakeMessages: GroupTalkTranscriptMessage[], completionMessages: GroupTalkTranscriptMessage[]) {
  const completionBody = completionMessages
    .filter((message) => message.phase !== "user" && message.phase !== "intake")
    .sort((left, right) => {
      if (left.sequence === null && right.sequence === null) return 0;
      if (left.sequence === null) return 1;
      if (right.sequence === null) return -1;
      return left.sequence - right.sequence;
    });

  return [...intakeMessages, ...completionBody];
}

export default function GroupTalkSession() {
  const { sessionId = "" } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const characterName = useCurrentCharacterName();
  const endRef = useRef<HTMLDivElement | null>(null);

  const [snapshot, setSnapshot] = useState<GroupTalkActiveSessionSnapshot | null>(() =>
    sessionId ? loadGroupTalkActiveSessionSnapshot(sessionId) : null
  );
  const [composerText, setComposerText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [pendingTurn, setPendingTurn] = useState<GroupTalkTurnView | null>(null);

  useEffect(() => {
    if (!sessionId) {
      navigate("/talk/group", {
        replace: true,
        state: { errorMessage: "会議セッションが見つからなかったため、設定画面からやり直してください。" },
      });
      return;
    }

    const loaded = loadGroupTalkActiveSessionSnapshot(sessionId);
    if (!loaded) {
      navigate("/talk/group", {
        replace: true,
        state: { errorMessage: "会議セッションを復元できなかったため、設定画面からやり直してください。" },
      });
      return;
    }

    setSnapshot(loaded);
  }, [navigate, sessionId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [pendingTurn, snapshot]);

  const allTurns = useMemo(() => {
    if (!snapshot) return pendingTurn ? [pendingTurn] : [];
    return pendingTurn ? [...snapshot.turns, pendingTurn] : snapshot.turns;
  }, [pendingTurn, snapshot]);

  const handleSend = async () => {
    const trimmed = composerText.trim();
    if (!snapshot || !trimmed || sending) return;

    setSending(true);
    setLoadError("");
    setComposerText("");
    const createdPendingTurn = createPendingTurn(trimmed);
    setPendingTurn(createdPendingTurn);

    try {
      const intakeResponse = await api.createGroupTalkIntake(snapshot.session.sessionId, {
        userMessage: trimmed,
      });

      const nextSession = normalizeGroupTalkSessionView(
        {
          ...snapshot.session,
          session_id: intakeResponse.session_id,
          template_id: snapshot.session.templateId,
          title: snapshot.session.title,
          description: snapshot.session.description,
          members: snapshot.session.members.map((member) => ({
            participant_id: member.participantId,
            member_id: member.memberId,
            speaker_id: member.speakerId,
            display_name: member.label,
            role: member.role,
            stance: member.stanceId,
          })),
          agreed_notes: intakeResponse.agreed_notes,
          session_state: intakeResponse.session_state,
          status: snapshot.session.status,
          created_at: snapshot.session.createdAt,
        },
        characterName
      );

      const intakeTurn = normalizeGroupTalkIntakeView(intakeResponse, nextSession, characterName);
      const fallbackUserMessages = createdPendingTurn.messages.filter((message) => message.phase === "user");
      const intakeMessages = intakeTurn.messages.length > 0 ? intakeTurn.messages : fallbackUserMessages;

      setPendingTurn({
        sessionId: intakeTurn.sessionId,
        turnId: intakeTurn.turnId,
        messages: intakeMessages,
        createdAt: intakeTurn.createdAt,
        isSatisfied: false,
      });

      if (intakeTurn.needsFollowUp || !intakeTurn.continueToken) {
        const nextTurn: GroupTalkTurnView = {
          sessionId: intakeTurn.sessionId,
          turnId: intakeTurn.turnId,
          messages: intakeMessages,
          createdAt: intakeTurn.createdAt,
          isSatisfied: false,
        };

        const nextSnapshot: GroupTalkActiveSessionSnapshot = {
          ...snapshot,
          session: {
            ...nextSession,
            agreedNotes: intakeTurn.agreedNotes,
            sessionState: intakeTurn.sessionState,
          },
          turns: [...snapshot.turns, nextTurn],
        };

        saveGroupTalkActiveSessionSnapshot(nextSnapshot);
        setSnapshot(nextSnapshot);
        setPendingTurn(null);

        return;
      }

      const completionResponse = await api.createGroupTalkTurnComplete(snapshot.session.sessionId, intakeTurn.turnId, {
        continueToken: intakeTurn.continueToken,
      });

      const completedSession = normalizeGroupTalkSessionView(
        {
          ...nextSession,
          session_id: completionResponse.session_id,
          template_id: nextSession.templateId,
          title: nextSession.title,
          description: nextSession.description,
          members: nextSession.members.map((member) => ({
            participant_id: member.participantId,
            member_id: member.memberId,
            speaker_id: member.speakerId,
            display_name: member.label,
            role: member.role,
            stance: member.stanceId,
          })),
          agreed_notes: completionResponse.agreed_notes,
          session_state: completionResponse.session_state,
          status: nextSession.status,
          created_at: nextSession.createdAt,
        },
        characterName
      );

      const completedTurn = normalizeGroupTalkTurnView(completionResponse, completedSession, characterName);

      const mergedMessages = mergeTurnMessages(intakeMessages, completedTurn.messages);
      const { initialMessages, expertMessages, summaryMessages } = splitMessagesForReveal(mergedMessages);
      const revealedInitialMessages = initialMessages.length > 0 ? initialMessages : fallbackUserMessages;

      setPendingTurn({
        ...completedTurn,
        messages: revealedInitialMessages,
      });

      for (const message of expertMessages) {
        await wait(EXPERT_MESSAGE_DELAY_MS);
        setPendingTurn((current) => {
          if (!current || current.turnId !== completedTurn.turnId) return current;
          return {
            ...current,
            messages: [...current.messages, message],
          };
        });
      }

      for (const message of summaryMessages) {
        await wait(SUMMARY_MESSAGE_DELAY_MS);
        setPendingTurn((current) => {
          if (!current || current.turnId !== completedTurn.turnId) return current;
          return {
            ...current,
            messages: [...current.messages, message],
          };
        });
      }

      const nextSnapshot: GroupTalkActiveSessionSnapshot = {
        ...snapshot,
        session: completedSession,
        turns: [
          ...snapshot.turns,
          {
            ...completedTurn,
            messages: mergedMessages,
          },
        ],
      };

      saveGroupTalkActiveSessionSnapshot(nextSnapshot);
      setSnapshot(nextSnapshot);
      setPendingTurn(null);
    } catch (e) {
      logError(e);
      setPendingTurn(null);
      setComposerText(trimmed);
      if (e instanceof AppError && e.code === "SESSION_EXPIRED") {
        clearGroupTalkActiveSessionSnapshot(snapshot.session.sessionId);
        navigate("/splash", { replace: true });
        return;
      }
      setLoadError(toUserMessage(e));
    } finally {
      setSending(false);
    }
  };

  if (!snapshot) {
    return null;
  }

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#FDF8F5] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-4xl flex-col">
        <header className="mb-4 flex items-center justify-between">
          <Link
            to="/talk/group/preview"
            state={{ draft: snapshot.draft }}
            className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]"
          >
            ← メンバー調整へ
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">{snapshot.session.title}</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="rounded-[2rem] border border-[#EADBC8] bg-white/90 p-4 shadow-[0_20px_48px_rgba(124,90,78,0.14)]">
          <h1 className="text-2xl font-black tracking-tight text-[#7C5A4E]">{snapshot.session.title}</h1>
          <p className="mt-2 text-sm leading-6 text-[#6B5851]">{snapshot.session.description}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {snapshot.session.members.map((member) => (
              <span
                key={member.participantId}
                className={`rounded-full border px-3 py-1.5 text-xs font-bold ${
                  member.facilitator
                    ? "border-[#E8CDBE] bg-[#FFF1E8] text-[#8D5B45]"
                    : "border-[#D9C5B9] bg-[#FFFBF8] text-[#5D4D46]"
                }`}
              >
                {member.label}
              </span>
            ))}
          </div>
        </section>

        <section className="mt-4 flex min-h-0 flex-1 flex-col rounded-[2rem] border border-[#EADBC8] bg-white px-4 py-4 shadow-[0_20px_48px_rgba(124,90,78,0.10)]">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
            {allTurns.flatMap((turn) =>
              turn.messages.map((message) => (
                <div
                  key={message.messageId}
                  className={message.speakerType === "user" ? "flex justify-end" : "flex justify-start"}
                >
                  <div
                    className={`max-w-[84%] lg:max-w-[68%] rounded-[1.6rem] px-4 py-3 ${
                      message.speakerType === "user"
                        ? "bg-[#5D4D46] text-white"
                        : message.facilitator
                          ? "border border-[#E8CDBE] bg-[#FFF1E8] text-[#4A3E39]"
                          : "border border-[#EADBC8] bg-[#FFFBF8] text-[#4A3E39]"
                    }`}
                  >
                    <p className={`text-xs font-black ${message.speakerType === "user" ? "text-[#F8EDE7]" : "text-[#A67C52]"}`}>
                      {message.speakerName}
                      {message.speakerType !== "user" ? ` / ${message.roleLabel}` : ""}
                    </p>
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-7">{message.content}</p>
                  </div>
                </div>
              ))
            )}
            <div ref={endRef} />
          </div>

          {loadError ? (
            <p role="alert" className="mt-4 text-sm font-bold text-[#C06D5A]">
              {loadError}
            </p>
          ) : null}

          <div className="mt-4 flex items-end gap-3 border-t border-[#F0E7DF] pt-4">
            <textarea
              value={composerText}
              onChange={(event) => setComposerText(event.target.value)}
              placeholder="相談したいことを入力..."
              rows={3}
              disabled={sending}
              className="min-h-[84px] flex-1 resize-none rounded-[1.4rem] border border-[#EADBC8] bg-[#FFFDFB] px-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
            />
            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={sending || !composerText.trim()}
              className="rounded-[1.4rem] bg-[#5D4D46] px-5 py-3 text-sm font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? "送信中..." : "送信"}
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}
